import { List, Map } from 'immutable';

import { getTemplateOperationsForProducts, shapeTemplateForApp } from '../utils/profiles/v2/templates';
import { shapeProfilesForApp, shapeProfileForAPI } from '../utils/profiles/v2/profiles';
import { getProfileSections, setAll } from '../utils/profiles/v2/sections';
import { shapeAiOperationsForApp } from '../utils/profiles/v2/ai';
import { shapeOperationsForAPI } from '../utils/product/shapeForAPI';
import { shapeListingsForApp } from '../utils/profiles/v2/listings';
import { isFeatureEnabled } from '../utils/featureFlags';
import { createController } from '../utils/controller';
import { getCopyTitle } from '../utils/profiles/getCopyTitle';
import { escapeRegex } from '../utils/string';
import { newProduct } from '../utils/new/product';
import { decrease } from '../utils/math';
import { getSize } from '../utils/iterable/getSize';
import api from '../utils/api';

import { ALL, ERRORS, PAGE_SIZE, PROFILE_TYPE, SAVE_TYPE } from '../constants/profiles';
import { POLLING_INTERVAL, SYNC_INDICATOR } from '../constants/shops';
import { MESSAGE, NOTIFICATION } from '../constants/notifications';
import { AMOUNT, ERROR, NEW } from '../constants/product';
import { CHANNEL_ID } from '../constants/channels';
import { FEATURES } from '../constants/billing';
import { RETAIN } from '../constants/variations';
import { STATUS } from '../constants/listings';
import { MODALS } from '../constants/modal';
import { EMPTY } from '../constants';
import { FORM } from '../constants/forms';
import ATTRIBUTE from '../constants/attributes';

import actions from '../actions';

const filtersController = createController();
const listingsController = createController();
const profilesController = createController();
const templatesController = createController();

function abort(controller) {
  if (controller.isActive()) {
    controller.stop();
  }
}

function abortAll() {
  [
    filtersController,
    listingsController,
    profilesController,
    templatesController,
  ].forEach(abort);
}

function* bootstrap(state, { shopId, type }) {
  abortAll();

  if (!shopId) {
    return state.set('profiles', EMPTY.MAP);
  }

  const shop = state.getIn(['shops', 'byId', shopId]);
  const channel = shop.get('channel');
  const db = shop.get('db');
  const profiles = Map({
    channel,
    db,
    filters: Map({ section: ALL, type }),
    pagination: Map({ page: 0, pageSize: PAGE_SIZE }),
    query: '',
    shopId,
  });

  yield function effect(dispatch) {
    dispatch(actions.data.getAllData(shopId));
    dispatch(actions.profilesV2.getFilters(true));
    dispatch(actions.profilesV2.getProfiles());
  };

  return state.set('profiles', profiles);
}

function* cleanUp(state) {
  abortAll();
  return state.set('profiles', EMPTY.MAP);
}

function* connectListings(state, { listings, profile: source, profileId }) {
  const profiles = state.get('profiles');
  const channel = profiles.get('channel');
  const db = profiles.get('db');
  const shopId = profiles.get('shopId');
  const profile = source || profiles.getIn(['byId', profileId]);
  const type = profile.get('type');

  switch (type) {
    case PROFILE_TYPE.AI: {
      yield function effect(dispatch) {
        function onConnectFullfilled() {
          function onPropagateFullfilled() {
            dispatch(
              actions.shops.setData({
                path: ['syncData', shopId],
                value: Map({ indicator: SYNC_INDICATOR.SYNC }),
              })
            );

            dispatch(
              actions.shops.rescheduleShopsPolling(
                state.getIn(['user', 'config', 'shopsPollingIntervalShort']) ||
                POLLING_INTERVAL.SHORT
              )
            );

            dispatch(actions.profilesV2.connectListings.fulfilled({ listings, profileId, shopId, type }));
          }

          function onPropagateRejected(error) {
            console.error(error);
            dispatch(
              actions.notifications.add({
                message: MESSAGE.FAIL.SYNC,
                type: NOTIFICATION.ERROR,
              })
            );
          }

          api.profiles.v2
            .propagateProfile({ profileId, type })
            .then(onPropagateFullfilled)
            .catch(onPropagateRejected);
        }

        function onConnectRejected(error) {
          console.error(error);
          dispatch(
            actions.notifications.add({
              message: MESSAGE.FAIL.SYNC,
              type: NOTIFICATION.ERROR,
            })
          );
        }

        api.profiles.v2
          .connectListings({ listings, profileId, type })
          .then(onConnectFullfilled)
          .catch(onConnectRejected);
      };

      if (state.getIn(['connectProfile', 'loading'])) return state;

      break;
    }

    case PROFILE_TYPE.STANDARD: {
      if (!profile.has('template')) {
        yield function effect(dispatch) {
          const templateId = profile.get('templateId');

          function onTemplateFullfilled(data) {
            const template = shapeTemplateForApp({
              channel,
              data,
              sections: profile.get('sections'),
              shopId,
              templateId,
            });

            dispatch(
              actions.profilesV2.connectListings({
                listings,
                profile: profile.set('template', template),
                profileId,
              })
            );
          }

          function onTemplateRejected(error) {
            dispatch(actions.profilesV2.connectListings.rejected({ error }));
          }

          api.products
            .getById({ channel, db, payload: { id: [templateId] }, shopId })
            .then(onTemplateFullfilled)
            .catch(onTemplateRejected);
        };
      } else {
        yield function effect(dispatch) {
          function onConnectFullfilled() {
            const operations = getTemplateOperationsForProducts({ channel, profile });

            if (getSize(operations) === 0) {
              dispatch(actions.profilesV2.connectListings.fulfilled({ listings, profileId, shopId, type }));
              return;
            }

            const payload = {
              channel_id: CHANNEL_ID[channel],
              operations: shapeOperationsForAPI({ channel, operations }),
              userDb: db,
              shopId,
            };

            function onPropagateFullfilled() {
              dispatch(
                actions.shops.setData({
                  path: ['syncData', shopId],
                  value: Map({ indicator: SYNC_INDICATOR.SYNC }),
                })
              );

              dispatch(
                actions.shops.rescheduleShopsPolling(
                  state.getIn(['user', 'config', 'shopsPollingIntervalShort']) ||
                  POLLING_INTERVAL.SHORT
                )
              );

              dispatch(actions.profilesV2.connectListings.fulfilled({ listings, profileId, shopId, type }));
            }

            function onPropagateRejected(error) {
              console.error(error);
              dispatch(
                actions.notifications.add({
                  message: MESSAGE.FAIL.SYNC,
                  type: NOTIFICATION.ERROR,
                })
              );
            }

            api.profiles.v2
              .propagateProfile({ payload, profileId, type })
              .then(onPropagateFullfilled)
              .catch(onPropagateRejected);
          }

          function onConnectRejected(error) {
            dispatch(actions.profilesV2.connectListings.rejected({ error }));
          }

          api.profiles.v2
            .connectListings({ listings, profileId, type })
            .then(onConnectFullfilled)
            .catch(onConnectRejected);
        };

        if (state.getIn(['connectProfile', 'loading'])) return state;
      }

      break;
    }

    default: {
      break;
    }
  }

  return state.setIn(['connectProfile', 'loading'], true);
}

function* connectListingsRejected(state, { error }) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.CONNECT_LISTINGS,
      })
    );
  };

  return state.has('connectProfile')
    ? state.deleteIn(['connectProfile', 'loading'])
    : state;
}

function* connectListingsFulfilled(source, { listings, profileId, shopId, type }) {
  yield function effect(dispatch) {
    dispatch(actions.data.getProfiles({ force: true, shopId, type }));
  };

  const state = source.has('connectProfile')
    ? source.delete('connectProfile')
    : source;

  if (
    state.getIn(['profiles', 'shopId']) !== shopId ||
    state.getIn(['profiles', 'filters', 'type']) !== type ||
    !state.hasIn(['profiles', 'byId', profileId])
  ) {
    return state;
  }

  function updateProfile(oldProfile) {
    function updateTotal(total) {
      return total + getSize(listings);
    }

    return oldProfile
      .setIn(['listings', 'hasMore'], true)
      .setIn(['listings', 'loading'], true)
      .deleteIn(['listings', 'page'])
      .updateIn(['listings', 'total'], updateTotal)
      .set('waitingForSync', true);
  }

  return state.updateIn(['profiles', 'byId', profileId], updateProfile);
}

function* copyProfile(state, { confirmed, profile: source, profileId, shopId: targetShopId }) {
  const profiles = state.get('profiles');
  const sourceShopId = profiles.get('shopId');
  const userShops = state.get('shops');
  let profile = source;

  if (!confirmed && sourceShopId === targetShopId && getSize(state.getIn(['profiles', 'editing'])) > 0) {
    yield function effect(dispatch) {
      dispatch(
        actions.profilesV2.setModal({
          action: actions.profilesV2.copyProfile({ confirmed: true, profile: source, profileId, shopId: targetShopId }),
          type: MODALS.PROFILES.CANCEL,
        })
      );
    };

    return state;
  } else if (state.getIn(['profiles', 'modal', 'type']) === MODALS.PROFILES.CANCEL) {
    yield function effect(dispatch) {
      dispatch(actions.profilesV2.setModal());
    };
  }

  if (!profile) {
    profile = profiles.getIn(['byId', profileId]).update('name', getCopyTitle);
  }

  const type = profile.get('type');

  switch (type) {
    case PROFILE_TYPE.AI: {
      yield function effect(dispatch) {
        const channel = userShops.getIn(['byId', targetShopId, 'channel']);
        const db = userShops.getIn(['byId', targetShopId, 'db']);
        const payload = shapeProfileForAPI({ channel, profile });

        api.profiles.v2
          .createProfile({ db, payload, shopId: targetShopId, type })
          .then(function onSucceeded(response) {
            dispatch(
              actions.profilesV2.copyProfile.fulfilled({
                profileId,
                response,
                sections: payload.sections,
                sourceShopId,
                targetShopId,
                type,
              })
            );
          })
          .catch(function onFailed(error) {
            dispatch(actions.profilesV2.copyProfile.rejected({ error, profileId, sourceShopId, type }));
          });
      };

      break;
    }

    case PROFILE_TYPE.STANDARD: {
      if (!profile.has('template')) {
        yield function effect(dispatch) {
          const templateId = profile.get('templateId');
          const channel = profiles.get('channel');
          const db = profiles.get('db');
          const shopId = profiles.get('shopId');

          api.products
            .getById({ channel, db, payload: { id: [templateId] }, shopId })
            .then(function onSucceeded(data) {
              const template = shapeTemplateForApp({
                channel,
                data,
                sections: profile.get('sections'),
                shopId,
                templateId,
              });

              dispatch(
                actions.profilesV2.copyProfile({
                  profile: profile.set('template', template),
                  profileId,
                  targetShopId,
                })
              );
            })
            .catch(function onFailed(error) {
              dispatch(actions.profilesV2.copyProfile.rejected({ error, profileId, shopId, type }));
            });
        };
      } else {
        yield function effect(dispatch) {
          const channel = userShops.getIn(['byId', targetShopId, 'channel']);
          const db = userShops.getIn(['byId', targetShopId, 'db']);
          const payload = shapeProfileForAPI({ channel, db, profile, shopId: targetShopId });

          api.profiles.v2
            .createProfile({ db, payload, shopId: targetShopId, type })
            .then(function onSucceeded(response) {
              dispatch(
                actions.profilesV2.copyProfile.fulfilled({
                  profileId,
                  response,
                  sections: payload.sections,
                  sourceShopId,
                  targetShopId,
                  type,
                })
              );
            })
            .catch(function onFailed(error) {
              dispatch(actions.profilesV2.copyProfile.rejected({ error, profileId, sourceShopId, type }));
            });
        };

        if (profiles.hasIn(['loading', profileId])) return state;
      }

      break;
    }

    default: {
      break;
    }
  }

  function updateLoading(loading = EMPTY.SET) {
    return loading.add(profileId);
  }

  return state
    .setIn(['profiles', 'editing'], EMPTY.MAP)
    .updateIn(['profiles', 'loading'], updateLoading);
}

function* copyProfileRejected(state, { error, profileId, sourceShopId, type }) {
  yield function effect(dispatch) {
    console.error(error);

    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.COPY_PROFILE,
      })
    );
  };

  function updateLoading(loading = EMPTY.SET) {
    return loading.delete(profileId);
  }

  if (
    state.getIn(['profiles', 'shopId']) !== sourceShopId ||
    state.getIn(['profiles', 'filters', 'type']) !== type ||
    !state.hasIn(['profiles', 'loading', profileId])
  ) {
    return state;
  }

  return state.updateIn(['profiles', 'loading'], updateLoading);
}

function* copyProfileFulfilled(state, { profileId, response, sections, sourceShopId, targetShopId, type }) {
  let id;
  let profiles = state.get('profiles');
  const actionsToDispatch = [actions.data.getProfiles({ force: true, shopId: targetShopId, type })];

  switch (type) {
    case PROFILE_TYPE.AI: {
      id = response.aiProfileId.id;
      break;
    }

    case PROFILE_TYPE.STANDARD: {
      id = response.profileId.id;
      break;
    }

    default: {
      break;
    }
  }

  function updateLoading(loading = EMPTY.SET) {
    return loading.delete(profileId);
  }

  if (profiles.get('shopId') === targetShopId && profiles.getIn(['filters', 'type']) === type) {
    profiles = profiles.set('editing', Map({ id }));

    actionsToDispatch.push(
      actions.profilesV2.getFilters(),
      actions.notifications.add({
        type: NOTIFICATION.SUCCESS,
        message: MESSAGE.SUCCESS.COPY_PROFILE,
      })
    );

    if (profiles.getIn(['filters', 'section']) === ALL || sections[profiles.getIn(['filters', 'section'])]) {
      if (profiles.getIn(['pagination', 'page']) === 0) {
        profiles = profiles.set('scroll', true);

        actionsToDispatch.push(actions.profilesV2.getProfiles());
      } else {
        actionsToDispatch.push(actions.profilesV2.setPage({ page: 0 }));
      }
    } else {
      actionsToDispatch.push(actions.profilesV2.setSection({ section: ALL }));
    }
  } else {
    actionsToDispatch.push(
      actions.notifications.add({
        profileId: id,
        profileType: type,
        shopId: targetShopId,
        shopName: state.getIn(['shops', 'byId', targetShopId, 'name']),
        type: NOTIFICATION.PROFILE_COPIED,
      })
    );

    if (
      profiles.get('shopId') === sourceShopId &&
      profiles.getIn(['filters', 'type']) === type &&
      profiles.hasIn(['loading', profileId])
    ) {
      profiles = profiles.update('loading', updateLoading);
    }
  }

  yield function effect(dispatch) {
    actionsToDispatch.forEach(dispatch);
  };

  return state.set('profiles', profiles);
}

function* deleteProfile(state, { confirmed, profileId }) {
  if (
    !confirmed &&
    getSize(state.getIn(['profiles', 'editing'])) > 0 &&
    state.getIn(['profiles', 'editing', 'id']) !== NEW && (
      state.getIn(['profiles', 'editing', 'id']) === profileId ||
      getSize(state.getIn(['profiles', 'editing']).delete('id')) > 0
    )
  ) {
    yield function effect(dispatch) {
      dispatch(
        actions.profilesV2.setModal({
          action: actions.profilesV2.deleteProfile({ confirmed: true, profileId }),
          type: MODALS.PROFILES.CANCEL,
        })
      );
    };

    return state;
  } else if (state.getIn(['profiles', 'modal', 'type']) === MODALS.PROFILES.CANCEL) {
    yield function effect(dispatch) {
      dispatch(actions.profilesV2.setModal());
    };
  }

  yield function effect(dispatch) {
    const type = state.getIn(['profiles', 'byId', profileId, 'type']);
    const shopId = state.getIn(['profiles', 'shopId']);

    api.profiles.v2
      .deleteProfile({ profileId, type })
      .then(function onSucceeded() {
        dispatch(actions.profilesV2.deleteProfile.fulfilled({ profileId, shopId, type }));
      })
      .catch(function onFailed(error) {
        dispatch(actions.profilesV2.deleteProfile.rejected({ error, profileId, shopId, type }));
      });
  };

  function updateLoading(loading = EMPTY.SET) {
    return loading.add(profileId);
  }

  function updateEditing(editing = EMPTY.MAP) {
    return (
      editing.has('id') &&
      editing.get('id') !== profileId && (
        editing.get('id') === NEW ||
        getSize(editing.delete('id')) === 0
      )
    )
      ? editing
      : EMPTY.MAP;
  }

  return state
    .updateIn(['profiles', 'loading'], updateLoading)
    .updateIn(['profiles', 'editing'], updateEditing);
}

function* deleteProfileRejected(state, { error, profileId, shopId, type }) {
  yield function effect(dispatch) {
    console.error(error);

    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.DELETE_PROFILE,
      })
    );
  };

  function updateLoading(loading = EMPTY.SET) {
    return loading.delete(profileId);
  }

  if (
    state.getIn(['profiles', 'shopId']) === shopId &&
    state.getIn(['profiles', 'filters', 'type']) === type &&
    state.hasIn(['profiles', 'loading', profileId])
  ) {
    return state.updateIn(['profiles', 'loading'], updateLoading);
  }

  return state;
}

function* deleteProfileFulfilled(state, { profileId, shopId, type }) {
  yield function effect(dispatch) {
    const actionsToDispatch = [
      actions.data.getProfiles({ force: true, shopId, type }),
      actions.notifications.add({
        type: NOTIFICATION.SUCCESS,
        message: MESSAGE.SUCCESS.DELETE_PROFILE,
      }),
    ];

    if (state.getIn(['profiles', 'shopId']) === shopId && state.getIn(['profiles', 'filters', 'type']) === type) {
      actionsToDispatch.push(actions.profilesV2.getFilters());

      if (state.hasIn(['profiles', 'loading', profileId])) {
        actionsToDispatch.push(actions.profilesV2.getProfiles());
      }
    }

    actionsToDispatch.forEach(dispatch);
  };

  return state;
}

function* disconnectListing(state, { index, productId, profileId }) {
  if (!state.hasIn(['profiles', 'byId', profileId])) return state;

  abort(listingsController);

  yield function effect(dispatch) {
    const { signal } = listingsController.start();
    const shopId = state.getIn(['profiles', 'shopId']);
    const type = state.getIn(['profiles', 'byId', profileId, 'type']);

    api.profiles.v2
      .disconnectListings({ listings: [productId], profileId, signal, type })
      .then(function onSucceeded() {
        dispatch(actions.profilesV2.disconnectListing.fulfilled({ index, productId, profileId, shopId, type }));
      })
      .catch(function onFailed(error) {
        dispatch(actions.profilesV2.disconnectListing.rejected({ error, productId, profileId, signal, shopId, type }));
      });
  };

  return state.setIn(['profiles', 'byId', profileId, 'listings', 'byId', productId, 'processing'], true);
}

function* disconnectListingRejected(state, { error, productId, profileId, signal, shopId, type }) {
  if (
    signal.aborted ||
    shopId !== state.getIn(['profiles', 'shopId']) ||
    type !== state.getIn(['profiles', 'filters', 'type'])
  ) {
    return state;
  }

  abort(listingsController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.DISCONNECT_LISTINGS,
      })
    );
  };

  if (state.hasIn(['profiles', 'byId', profileId, 'listings', 'byId', productId])) {
    return state.deleteIn(['profiles', 'byId', profileId, 'listings', 'byId', productId, 'processing']);
  }

  return state;
}

function* disconnectListingFulfilled(state, { index, productId, profileId, shopId, type }) {
  let data = state.get('data');
  let profiles = state.get('profiles');

  function updateIds(ids) {
    return ids.splice(index, 1);
  }

  function updateListings(source) {
    let listings = source
      .deleteIn(['byId', productId])
      .delete('loading')
      .update('total', decrease);

    if (listings.has('ids')) {
      listings = listings.update('ids', updateIds);
    }

    if (!listings.get('total')) {
      listings = listings.delete('animating').delete('opened');
    }

    return listings;
  }

  if (data.hasIn(['shopsData', shopId, 'profiles', type, 'byId', profileId])) {
    data = data.updateIn(['shopsData', shopId, 'profiles', type, 'byId', profileId, 'listings'], decrease);
  }

  if (
    shopId === profiles.get('shopId') &&
    type === profiles.getIn(['filters', 'type']) &&
    profiles.hasIn(['byId', profileId, 'listings'])
  ) {
    abort(listingsController);
    profiles = profiles.updateIn(['byId', profileId, 'listings'], updateListings);
  }

  return state.set('data', data).set('profiles', profiles);
}

function* edit(state, { confirmed, profileId }) {
  if (!confirmed && getSize(state.getIn(['profiles', 'editing'])) > 0) {
    yield function effect(dispatch) {
      dispatch(
        actions.profilesV2.setModal({
          action: actions.profilesV2.edit({ confirmed: true, profileId }),
          type: MODALS.PROFILES.CANCEL,
        })
      );
    };

    return state;
  } else if (state.getIn(['profiles', 'modal', 'type']) === MODALS.PROFILES.CANCEL) {
    yield function effect(dispatch) {
      dispatch(actions.profilesV2.setModal());
    };
  }

  if (profileId === NEW) {
    const channel = state.getIn(['profiles', 'channel']);
    const type = state.getIn(['profiles', 'filters', 'type']);
    let profile = Map({
      name: '',
      id: NEW,
      sections: setAll(getProfileSections({ channel, type })),
      type,
    });

    switch (type) {
      case PROFILE_TYPE.AI: {
        const ai = Map({
          [ATTRIBUTE.DESCRIPTION]: EMPTY.MAP,
          [ATTRIBUTE.TAGS]: EMPTY.MAP,
          [ATTRIBUTE.TITLE]: EMPTY.MAP,
        });

        profile = profile.set('ai', ai);
        break;
      }

      case PROFILE_TYPE.STANDARD: {
        const template = newProduct({ channel, emptyValues: true })
          .set(ERROR, EMPTY.MAP)
          .set('status', STATUS.TEMPLATE)
          .setIn([FORM.VARIATIONS, RETAIN], EMPTY.MAP)
          .setIn([FORM.VARIATIONS, 'currentProfile'], Map({ profileId: AMOUNT.MINUS_ONE }));

        profile = profile.set('template', template);
        break;
      }

      default: {
        break;
      }
    }

    return state.setIn(['profiles', 'editing'], profile);
  } else if (state.hasIn(['profiles', 'byId', profileId])) {
    const profile = state.getIn(['profiles', 'byId', profileId]);

    switch (profile.get('type')) {
      case PROFILE_TYPE.AI: {
        if (!profile.has('ai')) {
          yield function effect(dispatch) {
            dispatch(actions.profilesV2.getAiOperations({ profileIds: List([profileId]) }));
          };
        }

        break;
      }

      case PROFILE_TYPE.STANDARD: {
        if (!profile.has('template')) {
          const templateId = profile.get('templateId');

          yield function effect(dispatch) {
            dispatch(
              actions.profilesV2.getTemplates(
                Map({
                  profileIds: List([profileId]),
                  templateIds: List([templateId]),
                  templateToProfileMap: Map({ [templateId]: profileId }),
                })
              )
            );
          };
        }

        break;
      }

      default: {
        break;
      }
    }

    return state.setIn(['profiles', 'editing'], profile);
  }

  return state.setIn(['profiles', 'editing'], Map({ id: profileId }));
}

function* getAiOperations(state, { profileIds }) {
  if (getSize(profileIds) === 0) return state;

  abort(templatesController);
  const profiles = state.get('profiles');

  yield function effect(dispatch) {
    const { signal } = templatesController.start();

    function mapProfileIds(profileId) {
      return api.profiles.v2.getById({ profileId, signal, type: PROFILE_TYPE.AI });
    }

    const requests = profileIds.map(mapProfileIds);

    Promise.all(requests)
      .then(function onSucceeded(responses) {
        dispatch(actions.profilesV2.getAiOperations.fulfilled({ profileIds, responses }));
      })
      .catch(function onFailed(error) {
        console.error(error);
        dispatch(actions.profilesV2.getAiOperations.rejected({ error, profileIds, signal }));
      });
  };

  return state.setIn(['profiles', 'loading'], profiles.get('loading', EMPTY.SET).union(profileIds.toSet()));
}

function* getAiOperationsFulfilled(state, { profileIds, responses }) {
  abort(templatesController);

  function reduceResponses(result, response, index) {
    const profileId = profileIds.get(index);

    if (!result.hasIn(['byId', profileId])) return result;

    let profiles = result;
    const ai = shapeAiOperationsForApp(response.data);

    profiles = profiles
      .setIn(['byId', profileId, 'ai'], ai)
      .update('loading', function updateLoading(loading = EMPTY.SET) {
        return loading.delete(profileId);
      });

    if (profiles.getIn(['editing', 'id']) === profileId) {
      profiles = profiles.setIn(['editing', 'ai'], ai);
    }

    return profiles;
  }

  return state.set('profiles', responses.reduce(reduceResponses, state.get('profiles')));
}

function* getAiOperationsRejected(state, { error, profileIds, signal }) {
  if (signal.aborted) return state;

  abort(templatesController);

  yield function effect(dispatch) {
    if (error) console.error(error);

    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_PROFILE,
      })
    );
  };

  function updateEditing(editing = EMPTY.MAP) {
    const id = editing.get('id');

    if (id && profileIds.includes(id)) {
      return EMPTY.MAP;
    }

    return editing;
  }

  function updateLoading(loading = EMPTY.SET) {
    return loading.subtract(profileIds.toSet());
  }

  return state
    .updateIn(['profiles', 'editing'], updateEditing)
    .updateIn(['profiles', 'loading'], updateLoading);
}

function* getFilters(state, initial) {
  abort(filtersController);

  const profiles = state.get('profiles');
  const shopId = profiles.get('shopId');

  if (!shopId) {
    return state;
  }

  if (initial) {
    const db = profiles.get('db');
    const { signal } = filtersController.start();
    const requests = [api.profiles.v2.getFilters({ db, shopId, signal, type: PROFILE_TYPE.STANDARD })];

    if (isFeatureEnabled({ feature: FEATURES.AI_PROFILES, userId: state.getIn(['user', 'userId']) })) {
      requests.push(api.profiles.v2.getFilters({ db, shopId, signal, type: PROFILE_TYPE.AI }));
    } else {
      requests.push(Promise.resolve({ [ALL]: 0 }));
    }

    yield function effect(dispatch) {
      Promise
        .all(requests)
        .then(function onSucceeded(responses) {
          abort(filtersController);
          const [standard, ai] = responses;

          dispatch(
            actions.profilesV2.getFilters.fulfilled({
              initial,
              response: standard,
              type: PROFILE_TYPE.STANDARD,
            })
          );

          dispatch(actions.profilesV2.getFilters.fulfilled({ response: ai, type: PROFILE_TYPE.AI }));
        })
        .catch(function onFailed(error) {
          if (!signal.aborted) {
            abort(filtersController);
            dispatch(actions.profilesV2.getFilters.rejected({ error }));
          }
        });
    };
  } else {
    const type = profiles.getIn(['filters', 'type']);

    if (
      type === PROFILE_TYPE.AI &&
      !isFeatureEnabled({ feature: FEATURES.AI_PROFILES, userId: state.getIn(['user', 'userId']) })
    ) {
      return state
        .setIn(['profiles', 'filters', 'section'], ALL)
        .setIn(['profiles', 'filters', 'sections'], Map({ [ALL]: 0 }))
        .setIn(['profiles', 'filters', 'types', PROFILE_TYPE.AI], 0);
    }

    yield function effect(dispatch) {
      const db = profiles.get('db');
      const { signal } = filtersController.start();

      api.profiles.v2
        .getFilters({ db, shopId, signal, type })
        .then(function onSucceeded(response) {
          abort(filtersController);
          dispatch(actions.profilesV2.getFilters.fulfilled({ response, type }));
        })
        .catch(function onFailed(error) {
          if (!signal.aborted) {
            abort(filtersController);
            dispatch(actions.profilesV2.getFilters.rejected({ error }));
          }
        });
    };
  }

  return state.setIn(['profiles', 'filters', 'loading'], true);
}

function* getFiltersRejected(state, { error }) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_PROFILES,
      })
    );
  };

  return state.deleteIn(['profiles', 'filters', 'loading']);
}

function* getFiltersFulfilled(state, { response, type }) {
  const count = response[ALL] || 0;
  let profiles = state
    .get('profiles')
    .deleteIn(['filters', 'loading'])
    .setIn(['filters', 'types', type], count);

  if (profiles.getIn(['filters', 'type']) === type) {
    profiles = profiles.setIn(['filters', 'sections'], Map(response));
  }

  return state.set('profiles', profiles);
}

function* getListings(state, profileId) {
  if (!state.hasIn(['profiles', 'byId', profileId])) return state;

  abort(listingsController);

  const listings = state.getIn(['profiles', 'byId', profileId, 'listings']);
  const page = listings.get('page', -1) + 1;
  const limit = PAGE_SIZE;
  const offset = page * limit;

  if (offset >= listings.get('total')) return state;

  yield function effect(dispatch) {
    const { signal } = listingsController.start();
    const type = state.getIn(['profiles', 'byId', profileId, 'type']);

    api.profiles.v2
      .getListings({ limit, offset, profileId, signal, type })
      .then(function onSucceeded(response) {
        dispatch(actions.profilesV2.getListings.fulfilled({ page, profileId, response }));
      })
      .catch(function onFailed(error) {
        dispatch(actions.profilesV2.getListings.rejected({ error, profileId, signal }));
      });
  };

  return state.setIn(['profiles', 'byId', profileId, 'listings', 'loading'], true);
}

function* getListingsRejected(state, { error, profileId, signal }) {
  if (signal.aborted || !state.hasIn(['profiles', 'byId', profileId])) return state;

  abort(listingsController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_LISTINGS,
      })
    );
  };

  return state.deleteIn(['profiles', 'byId', profileId, 'listings', 'loading']);
}

function* getListingsFulfilled(state, { page, profileId, response }) {
  if (!state.hasIn(['profiles', 'byId', profileId])) return state;

  abort(listingsController);

  function updateListings(listings) {
    return shapeListingsForApp({ response, state: listings.delete('loading').set('page', page) });
  }

  return state.updateIn(['profiles', 'byId', profileId, 'listings'], updateListings);
}

function* getProfiles(state) {
  abort(profilesController);
  abort(templatesController);

  const profiles = state.get('profiles');
  const shopId = profiles.get('shopId');
  const type = profiles.getIn(['filters', 'type']);

  if (!shopId) {
    return state;
  }

  if (
    type === PROFILE_TYPE.AI &&
    !isFeatureEnabled({ feature: FEATURES.AI_PROFILES, userId: state.getIn(['user', 'userId']) })
  ) {
    return state
      .setIn(['profiles', 'byId'], EMPTY.MAP)
      .setIn(['profiles', 'ids'], EMPTY.LIST)
      .setIn(['profiles', 'loading'], EMPTY.SET)
      .setIn(['profiles', 'request'], EMPTY.MAP)
      .setIn(['profiles', 'total'], 0)
      .setIn(['profiles', 'section'], ALL)
      .setIn(['profiles', 'editing'], EMPTY.MAP)
      .setIn(['profiles', 'pagination'], Map({ page: 0, pageSize: PAGE_SIZE }));
  }

  yield function effect(dispatch) {
    const db = profiles.get('db');
    const limit = profiles.getIn(['pagination', 'pageSize']);
    const offset = profiles.getIn(['pagination', 'page']) * limit;
    const section = profiles.getIn(['filters', 'section']);
    const name = profiles.get('query');
    const request = { db, limit, name, offset, section, shopId, type };
    const { signal } = profilesController.start();

    api.profiles.v2
      .getProfiles({ ...request, signal })
      .then(function onSucceeded(response) {
        dispatch(actions.profilesV2.getProfiles.fulfilled({ request, response }));
      })
      .catch(function onFailed(error) {
        dispatch(actions.profilesV2.getProfiles.rejected({ error, signal }));
      });
  };

  return state.setIn(['profiles', 'loading'], profiles.get('ids', EMPTY.LIST).toSet());
}

function* getProfilesRejected(state, { error, signal }) {
  if (signal.aborted) return state;

  abort(profilesController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_PROFILES,
      })
    );
  };

  return state.setIn(['profiles', 'loading'], EMPTY.SET);
}

function* getProfilesFulfilled(state, { request, response }) {
  abort(profilesController);

  if (!response.count) {
    if (
      request.section !== ALL &&
      state.getIn(['profiles', 'request', 'section'], state.getIn(['profiles', 'filters', 'section'])) !== ALL
    ) {
      yield function effect(dispatch) {
        dispatch(actions.profilesV2.setSection({ section: ALL }));
      };

      return state;
    }

    if (
      request.offset > 0 &&
      state.getIn(['profiles', 'pagination', 'page'], 0) > 0
    ) {
      yield function effect(dispatch) {
        dispatch(actions.profilesV2.setPage({ page: 0 }));
      };

      return state;
    }
  } else if (getSize(response.data) === 0) {
    if (state.getIn(['profiles', 'pagination', 'page'], 0) > 0) {
      yield function effect(dispatch) {
        dispatch(actions.profilesV2.setPage({ page: Math.max(0, Math.floor((response.count - 1) / PAGE_SIZE)) }));
      };

      return state;
    }
  }

  const {
    actions: actionsToDispatch,
    profiles,
  } = shapeProfilesForApp({ profiles: state.get('profiles'), request, response });

  if (getSize(actionsToDispatch) > 0) {
    yield function effect(dispatch) {
      actionsToDispatch.forEach(dispatch);
    };
  }

  return state.set('profiles', profiles);
}

function* getTemplates(state, templates) {
  const profiles = state.get('profiles');

  if (getSize(templates) === 0) return state;

  const profileIds = templates.get('profileIds');
  const templateIds = templates.get('templateIds');
  const templateToProfileMap = templates.get('templateToProfileMap');

  if (getSize(templateIds) === 0) return state;

  abort(templatesController);

  yield function effect(dispatch) {
    const { signal } = templatesController.start();
    const channel = profiles.get('channel');
    const db = profiles.get('db');
    const shopId = profiles.get('shopId');

    api.products
      .getById({ channel, db, payload: { id: templateIds.toArray() }, shopId, signal })
      .then(function onSucceeded(data) {
        dispatch(
          actions.profilesV2.getTemplates.fulfilled({
            channel,
            data,
            shopId,
            signal,
            templateIds,
            templateToProfileMap,
          })
        );
      })
      .catch(function onFailed(error) {
        dispatch(actions.profilesV2.getTemplates.rejected({ error, profileIds, signal }));
      });
  };

  return state.setIn(['profiles', 'loading'], profiles.get('loading', EMPTY.SET).union(profileIds.toSet()));
}

function* getTemplatesRejected(state, { error, profileIds, signal }) {
  if (signal.aborted) return state;

  abort(templatesController);

  yield function effect(dispatch) {
    if (error) console.error(error);

    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_PROFILE,
      })
    );
  };

  function updateEditing(editing = EMPTY.MAP) {
    const id = editing.get('id');

    if (id && profileIds.includes(id)) {
      return EMPTY.MAP;
    }

    return editing;
  }

  function updateLoading(loading = EMPTY.SET) {
    return loading.subtract(profileIds.toSet());
  }

  return state
    .updateIn(['profiles', 'editing'], updateEditing)
    .updateIn(['profiles', 'loading'], updateLoading);
}

function* getTemplatesFulfilled(state, { channel, data, shopId, signal, templateIds, templateToProfileMap }) {
  let profiles = state.get('profiles');
  let failed = EMPTY.LIST;

  for (const templateId of templateIds) {
    const profileId = templateToProfileMap.get(templateId);

    if (!profiles.hasIn(['byId', profileId])) continue;

    try {
      const sections = profiles.getIn(['byId', profileId, 'sections']);
      const template = shapeTemplateForApp({ channel, data, sections, shopId, templateId });

      profiles = profiles
        .setIn(['byId', profileId, 'template'], template)
        .update('loading', function updateLoading(loading = EMPTY.SET) {
          return loading.delete(profileId);
        });

      if (profiles.getIn(['editing', 'id']) === profileId) {
        profiles = profiles.setIn(['editing', 'template'], template);
      }
    } catch (error) {
      console.error(error);
      failed = failed.push(profileId);
    }
  }

  if (getSize(failed)) {
    yield function effect(dispatch) {
      dispatch(actions.profilesV2.getTemplates.rejected({ profileIds: failed, signal }));
    };
  } else {
    abort(templatesController);
  }

  return state.set('profiles', profiles);
}

function* saveProfile(state, { type: saveType }) {
  const profiles = state.get('profiles');
  const profile = profiles.get('editing');
  const profileId = profile.get('id');
  const type = profile.get('type');

  yield function effect(dispatch) {
    const db = profiles.get('db');
    const shopId = profiles.get('shopId');
    const channel = profiles.get('channel');
    const payload = shapeProfileForAPI({ channel, db, profile, shopId });

    function onFailed(error) {
      dispatch(actions.profilesV2.saveProfile.rejected({ error, profile, profileId, saveType, shopId, type }));
    }

    function onSucceeded(response) {
      dispatch(
        actions.profilesV2.saveProfile.fulfilled({
          channel,
          db,
          profile,
          profileId,
          response,
          saveType,
          sections: payload.sections,
          shopId,
          type,
        })
      );
    }

    switch (saveType) {
      case SAVE_TYPE.SAVE_AS_NEW: {
        api.profiles.v2
          .createProfile({ db, payload, shopId, type })
          .then(onSucceeded)
          .catch(onFailed);

        break;
      }

      default: {
        api.profiles.v2
          .updateProfile({ payload, profileId, type })
          .then(onSucceeded)
          .catch(onFailed);

        break;
      }
    }
  };

  function updateLoading(loading = EMPTY.SET) {
    return loading.add(profileId);
  }

  return state.updateIn(['profiles', 'loading'], updateLoading);
}

function* saveProfileRejected(state, { error, profile, profileId, saveType, shopId, type }) {
  function updateEditing(editing = EMPTY.MAP) {
    return editing.setIn([ERROR, 'name'], ERRORS.NAME.SAME_NAME_PROFILE);
  }

  function updateLoading(loading = EMPTY.SET) {
    return loading.delete(profileId);
  }

  if (
    state.getIn(['profiles', 'shopId']) === shopId &&
    state.getIn(['profiles', 'filters', 'type']) === type &&
    state.getIn(['profiles', 'editing', 'id']) === profileId
  ) {
    if (typeof error === 'string' && new RegExp(escapeRegex(profile.get('name'))).test(error)) {
      return state
        .updateIn(['profiles', 'editing'], updateEditing)
        .updateIn(['profiles', 'loading'], updateLoading);
    }
  }

  yield function effect(dispatch) {
    console.error(error);

    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: saveType === SAVE_TYPE.SAVE_AS_NEW
          ? MESSAGE.FAIL.CREATE_PROFILE
          : MESSAGE.FAIL.SAVE_PROFILE,
      })
    );
  };

  if (
    state.getIn(['profiles', 'shopId']) !== shopId ||
    state.getIn(['profiles', 'filters', 'type']) !== type ||
    !state.hasIn(['profiles', 'loading', profileId])
  ) {
    return state;
  }

  return state.updateIn(['profiles', 'loading'], updateLoading);
}

function* saveProfileFulfilled(state, { channel, db, profile, profileId, response, saveType, sections, shopId, type }) {
  let profiles = state.get('profiles');
  const actionsToDispatch = [
    actions.data.getProfiles({ force: true, shopId, type }),
    actions.notifications.add({
      type: NOTIFICATION.SUCCESS,
      message: saveType === SAVE_TYPE.SAVE_AS_NEW
        ? MESSAGE.SUCCESS.CREATE_PROFILE
        : MESSAGE.SUCCESS.SAVE_PROFILE,
    }),
  ];

  if (profiles.get('shopId') === shopId && profiles.getIn(['filters', 'type']) === type) {
    if (profiles.getIn(['editing', 'id']) === profileId) {
      profiles = profiles.set('editing', EMPTY.MAP);
    }

    if (saveType === SAVE_TYPE.UPDATE_PROFILE && profiles.hasIn(['byId', profileId])) {
      switch (type) {
        case PROFILE_TYPE.AI: {
          profiles = profiles.deleteIn(['byId', profileId, 'ai']);
          break;
        }

        case PROFILE_TYPE.STANDARD: {
          profiles = profiles.deleteIn(['byId', profileId, 'template']);
          break;
        }

        default: {
          break;
        }
      }
    }

    actionsToDispatch.push(actions.profilesV2.getFilters());

    if (profiles.getIn(['filters', 'section']) === ALL || sections[profiles.getIn(['filters', 'section'])]) {
      if (saveType === SAVE_TYPE.SAVE_AS_NEW) {
        if (profiles.getIn(['pagination', 'page']) === 0) {
          let id;
          let newProfile = profile.set('listings', Map({ total: 0 }));

          switch (type) {
            case PROFILE_TYPE.AI: {
              id = response.aiProfileId.id;
              newProfile = newProfile.set('id', id);
              break;
            }

            case PROFILE_TYPE.STANDARD: {
              id = response.profileId.id;
              newProfile = newProfile.delete('template').set('id', id);
              break;
            }

            default: {
              break;
            }
          }

          profiles = profiles
            .set('scroll', true)
            .set('ids', profiles.get('ids').unshift(id))
            .set('loading', profiles.get('loading', EMPTY.SET).add(id))
            .setIn(['byId', id], newProfile);

          actionsToDispatch.push(actions.profilesV2.getProfiles());
        } else {
          actionsToDispatch.push(actions.profilesV2.setPage({ page: 0 }));
        }
      } else {
        actionsToDispatch.push(actions.profilesV2.getProfiles());
      }
    } else {
      actionsToDispatch.push(actions.profilesV2.setSection({ section: ALL }));
    }
  }

  if (saveType === SAVE_TYPE.UPDATE_PROFILE && profile.getIn(['listings', 'total']) > 0) {
    yield function effect(dispatch) {
      const payload = {
        channel_id: CHANNEL_ID[channel],
        userDb: db,
        shopId,
      };

      if (type === PROFILE_TYPE.STANDARD) {
        const operations = getTemplateOperationsForProducts({ channel, profile });

        if (getSize(operations) === 0) {
          return;
        }

        payload.operations = shapeOperationsForAPI({ channel, operations });
      }

      api.profiles.v2
        .propagateProfile({ payload, profileId, type })
        .then(function onSucceeded() {
          dispatch(
            actions.shops.setData({
              path: ['syncData', shopId],
              value: Map({ indicator: SYNC_INDICATOR.SYNC }),
            })
          );

          dispatch(
            actions.shops.rescheduleShopsPolling(
              state.getIn(['user', 'config', 'shopsPollingIntervalShort']) ||
              POLLING_INTERVAL.SHORT
            )
          );
        })
        .catch(function onFailed(error) {
          console.error(error);
          dispatch(
            actions.notifications.add({
              message: MESSAGE.FAIL.SYNC,
              type: NOTIFICATION.ERROR,
            })
          );
        });
    };
  }

  yield function effect(dispatch) {
    actionsToDispatch.forEach(dispatch);
  };

  return state.set('profiles', profiles);
}

function* search(state, { confirmed, query }) {
  if (
    !confirmed &&
    getSize(state.getIn(['profiles', 'editing'])) > 0 &&
    state.getIn(['profiles', 'editing', 'id']) !== NEW &&
    getSize(state.getIn(['profiles', 'editing']).delete('id')) > 0
  ) {
    yield function effect(dispatch) {
      dispatch(
        actions.profilesV2.setModal({
          action: actions.profilesV2.search({ confirmed: true, query }),
          type: MODALS.PROFILES.CANCEL,
        })
      );
    };

    return state;
  } else if (state.getIn(['profiles', 'modal', 'type']) === MODALS.PROFILES.CANCEL) {
    yield function effect(dispatch) {
      dispatch(actions.profilesV2.setModal());
    };
  }

  yield function effect(dispatch) {
    dispatch(actions.profilesV2.getProfiles());
  };

  function updateEditing(editing = EMPTY.MAP) {
    return editing.has('id') && (
      editing.get('id') === NEW ||
      getSize(editing.delete('id')) === 0
    )
      ? editing
      : EMPTY.MAP;
  }

  return state
    .setIn(['profiles', 'pagination', 'page'], 0)
    .setIn(['profiles', 'scroll'], true)
    .setIn(['profiles', 'query'], query)
    .updateIn(['profiles', 'editing'], updateEditing);
}

function* setData(source, { path, value }) {
  const fullPath = Array.isArray(path)
    ? ['profiles', ...path]
    : ['profiles', path];

  if (value !== undefined) return source.setIn(fullPath, value);

  let state = source.deleteIn(fullPath);

  fullPath.pop();

  while (getSize(fullPath) > 1) {
    if (!getSize(state.getIn(fullPath))) {
      state = state.deleteIn(fullPath);
    }

    fullPath.pop();
  }

  return state;
}

function* setModal(state, payload) {
  return payload
    ? state.setIn(['profiles', 'modal'], Map(payload))
    : state.deleteIn(['profiles', 'modal']);
}

function* setPage(state, { confirmed, page }) {
  if (
    !confirmed &&
    getSize(state.getIn(['profiles', 'editing'])) > 0 &&
    state.getIn(['profiles', 'editing', 'id']) !== NEW &&
    getSize(state.getIn(['profiles', 'editing']).delete('id')) > 0
  ) {
    yield function effect(dispatch) {
      dispatch(
        actions.profilesV2.setModal({
          action: actions.profilesV2.setPage({ confirmed: true, page }),
          type: MODALS.PROFILES.CANCEL,
        })
      );
    };

    return state;
  } else if (state.getIn(['profiles', 'modal', 'type']) === MODALS.PROFILES.CANCEL) {
    yield function effect(dispatch) {
      dispatch(actions.profilesV2.setModal());
    };
  }

  yield function effect(dispatch) {
    dispatch(actions.profilesV2.getProfiles());
  };

  function updateEditing(editing = EMPTY.MAP) {
    return editing.has('id') && (
      editing.get('id') === NEW ||
      getSize(editing.delete('id')) === 0
    )
      ? editing
      : EMPTY.MAP;
  }

  return state
    .setIn(['profiles', 'scroll'], true)
    .setIn(['profiles', 'pagination', 'page'], page)
    .updateIn(['profiles', 'editing'], updateEditing);
}

function* setSection(state, { confirmed, section }) {
  if (
    !confirmed &&
    getSize(state.getIn(['profiles', 'editing'])) > 0 &&
    state.getIn(['profiles', 'editing', 'id']) !== NEW &&
    getSize(state.getIn(['profiles', 'editing']).delete('id')) > 0
  ) {
    yield function effect(dispatch) {
      dispatch(
        actions.profilesV2.setModal({
          action: actions.profilesV2.setSection({ confirmed: true, section }),
          type: MODALS.PROFILES.CANCEL,
        })
      );
    };

    return state;
  } else if (state.getIn(['profiles', 'modal', 'type']) === MODALS.PROFILES.CANCEL) {
    yield function effect(dispatch) {
      dispatch(actions.profilesV2.setModal());
    };
  }

  yield function effect(dispatch) {
    dispatch(actions.profilesV2.getProfiles());
  };

  function updateEditing(editing = EMPTY.MAP) {
    return editing.has('id') && (
      editing.get('id') === NEW ||
      getSize(editing.delete('id')) === 0
    )
      ? editing
      : EMPTY.MAP;
  }

  return state
    .setIn(['profiles', 'scroll'], true)
    .setIn(['profiles', 'pagination', 'page'], 0)
    .setIn(['profiles', 'filters', 'section'], section)
    .updateIn(['profiles', 'editing'], updateEditing);
}

function* setType(state, { type }) {
  yield function effect(dispatch) {
    dispatch(actions.profilesV2.getFilters());
    dispatch(actions.profilesV2.getProfiles());
  };

  return state
    .deleteIn(['profiles', 'modal'])
    .setIn(['profiles', 'editing'], EMPTY.MAP)
    .setIn(['profiles', 'filters', 'section'], ALL)
    .setIn(['profiles', 'filters', 'type'], type)
    .setIn(['profiles', 'pagination', 'page'], 0)
    .setIn(['profiles', 'scroll'], true);
}

function* showConnectModal(state, { profileId }) {
  yield function effect(dispatch) {
    const profiles = state.get('profiles');
    const channel = profiles.get('channel');
    const db = profiles.get('db');
    const shopId = profiles.get('shopId');
    const profile = profiles.getIn(['byId', profileId]);

    dispatch(actions.connectProfile.bootstrap({ channel, db, profileId, shopId }));

    if (profile.getIn(['listings', 'total']) > 0) {
      const listings = profile.getIn(['listings', 'ids']);

      if (getSize(listings) === profile.getIn(['listings', 'total'])) {
        dispatch(actions.connectProfile.setConnectedProducts(listings));
      } else {
        const { signal } = listingsController.start();

        api.profiles.v2
          .getListings({ limit: 'null', offset: 'null', profileId, signal, type: profile.get('type') })
          .then(function onSucceeded(response) {
            dispatch(actions.profilesV2.showConnectModal.fulfilled({ profileId, response }));
          })
          .catch(function onFailed(error) {
            dispatch(actions.profilesV2.showConnectModal.rejected({ error, profileId, signal }));
          });
      }
    } else {
      dispatch(actions.connectProfile.setConnectedProducts(EMPTY.LIST));
    }
  };

  return state;
}

function* showConnectModalRejected(state, { error, profileId, signal }) {
  if (signal.aborted || !state.has('connectProfile')) return state;

  yield function effect(dispatch) {
    dispatch(actions.profilesV2.getListings.rejected({ error, profileId, signal }));
    dispatch(actions.connectProfile.setConnectedProducts(EMPTY.LIST));
  };

  return state;
}

function* showConnectModalFulfilled(state, { profileId, response }) {
  abort(listingsController);

  if (!state.hasIn(['profiles', 'byId', profileId])) return state;

  const listings = shapeListingsForApp({
    response,
    state: state
      .getIn(['profiles', 'byId', profileId, 'listings'])
      .delete('loading')
      .set('page', 0),
  });

  if (state.has('connectProfile')) {
    yield function effect(dispatch) {
      dispatch(actions.connectProfile.setConnectedProducts(listings.get('ids')));
    };
  }

  return state.setIn(['profiles', 'byId', profileId, 'listings'], listings);
}

const profilesV2 = {
  [actions.profilesV2.bootstrap]: bootstrap,
  [actions.profilesV2.cleanUp]: cleanUp,
  [actions.profilesV2.connectListings]: connectListings,
  [actions.profilesV2.connectListings.fulfilled]: connectListingsFulfilled,
  [actions.profilesV2.connectListings.rejected]: connectListingsRejected,
  [actions.profilesV2.copyProfile]: copyProfile,
  [actions.profilesV2.copyProfile.fulfilled]: copyProfileFulfilled,
  [actions.profilesV2.copyProfile.rejected]: copyProfileRejected,
  [actions.profilesV2.deleteProfile]: deleteProfile,
  [actions.profilesV2.deleteProfile.fulfilled]: deleteProfileFulfilled,
  [actions.profilesV2.deleteProfile.rejected]: deleteProfileRejected,
  [actions.profilesV2.disconnectListing]: disconnectListing,
  [actions.profilesV2.disconnectListing.fulfilled]: disconnectListingFulfilled,
  [actions.profilesV2.disconnectListing.rejected]: disconnectListingRejected,
  [actions.profilesV2.edit]: edit,
  [actions.profilesV2.getAiOperations]: getAiOperations,
  [actions.profilesV2.getAiOperations.fulfilled]: getAiOperationsFulfilled,
  [actions.profilesV2.getAiOperations.rejected]: getAiOperationsRejected,
  [actions.profilesV2.getFilters]: getFilters,
  [actions.profilesV2.getFilters.fulfilled]: getFiltersFulfilled,
  [actions.profilesV2.getFilters.rejected]: getFiltersRejected,
  [actions.profilesV2.getListings]: getListings,
  [actions.profilesV2.getListings.fulfilled]: getListingsFulfilled,
  [actions.profilesV2.getListings.rejected]: getListingsRejected,
  [actions.profilesV2.getProfiles]: getProfiles,
  [actions.profilesV2.getProfiles.fulfilled]: getProfilesFulfilled,
  [actions.profilesV2.getProfiles.rejected]: getProfilesRejected,
  [actions.profilesV2.getTemplates]: getTemplates,
  [actions.profilesV2.getTemplates.fulfilled]: getTemplatesFulfilled,
  [actions.profilesV2.getTemplates.rejected]: getTemplatesRejected,
  [actions.profilesV2.saveProfile]: saveProfile,
  [actions.profilesV2.saveProfile.fulfilled]: saveProfileFulfilled,
  [actions.profilesV2.saveProfile.rejected]: saveProfileRejected,
  [actions.profilesV2.search]: search,
  [actions.profilesV2.setData]: setData,
  [actions.profilesV2.setModal]: setModal,
  [actions.profilesV2.setPage]: setPage,
  [actions.profilesV2.setSection]: setSection,
  [actions.profilesV2.setType]: setType,
  [actions.profilesV2.showConnectModal]: showConnectModal,
  [actions.profilesV2.showConnectModal.fulfilled]: showConnectModalFulfilled,
  [actions.profilesV2.showConnectModal.rejected]: showConnectModalRejected,
};

export default profilesV2;
